import { MobXProviderContext } from "mobx-react";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { Column } from "primereact/column";
import { confirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import { DataView } from "primereact/dataview";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from 'primereact/toast';
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getErrorTitle, getToastMessageForSubscriptionLimitReached, USER_GROUP_ADMIN } from "../../../constants/constants";
import { RESOURCE_LINK_TYPE } from "../../../constants/enum";
import { ROLE_TYPE_ADMIN, ROLE_TYPE_PROVIDER } from "../../../constants/RegisterConstants";
import { RouterAddress } from "../../../constants/RouterAddress";
import { SHOW_ADL_ENTRY_POINTS } from "../../../constants/uiFlags";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import { BusinessHomeProfile, ClientAvailabilityProfile, HomeHiringJobProfile, WebsiteInquiry } from "../../../models/businessHomeProfile";
import { ResidentProfile } from "../../../models/residentProfile";
import { UserProfile } from "../../../models/userProfile";
import { calculateAge, getQrCodeDataUrl, getWebsiteName, getWebsiteUrlForHome } from "../../../utils/stringUtils";
import { normalizePlainText, openUrlInCurrentTab, openUrlInNewTab, sanitizeWebsiteSlug } from "../../../utils/securityUtils";
import HomeProfile from "../../../components/general/homeProfile";
import Unauthorized from "../../../shared/pages/Unauthorized";
import AddResidentModal from "../components/AddResidentModal";
import CaregiverHiringProfileModal from "../components/CaregiverHiringProfileModal";
import ClientAvailabilityProfileModal from "../components/ClientAvailabilityProfileModal";
import EditHomeProfileModal from "../components/EditHomeProfileModal";
import LinkedLookupModal from "../components/LinkedLookupModal";
import UserFeedbackModal from "../components/UserFeedbackModal";
import WebsiteInquiriesModal from "../components/WebsiteInquiriesModal";
import WebsiteStatisticsModal from "../components/WebsiteStatisticsModal";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import {
  appAccentPanelStyle,
  appConfirmDialogClasses,
  appDataTableWrapperStyle,
  appDialogContentStyle,
  appDialogHeaderStyle,
  appDialogStyle,
  appInnerCardStyle,
  appMutedTextStyle,
  appNeutralPanelStyle,
  renderConfirmMessage,
  renderDialogFooter,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";
import { dashboardPageContainerStyle, dashboardSectionCardStyle, renderDashboardHeader } from "../../dashboard/pages/dashboardStyles";
import { useTour } from "../../../shared/tour/TourProvider";
import SubscribeForUnlimitedAccessDialog from "../../../components/general/SubscribeForUnlimitedAccessDialog";
import { DEFAULT_WEBSITE_TEMPLATE_KEY, WEBSITE_TEMPLATE_OPTIONS } from "../../publishWeb/components/websiteTemplates";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";

const HomeDetails = () => {
  const toast = useRef<Toast>(null);
  const { homeId } = useParams();
  const navigate = useNavigate();
  const mobile = useResponsive().isMobile;
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const authExperience = useAuthExperience();
  const { completeStep, isStepActive, currentStepId } = useTour();
  const [activeAccordionIndex, setActiveAccordionIndex] = useState<number[]>([0]);
  const [businessData, setBusinessData] = useState<BusinessHomeProfile | null>(
    null,
  );

  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [editData, setEditData] = useState<BusinessHomeProfile | null>(null);
  const [flag, setFlag] = useState<boolean>(false);
  const [profileLoading, setProfileLoading] = useState<boolean>(false);

  const [residents, setResidents] = useState<ResidentProfile[] | null>(null);
  const [residentsLoading, setResidentsLoading] = useState<boolean>(false);

  const [residentFlag, setResidentFlag] = useState<boolean>(false);
  const [openAddResidentModal, setOpenAddResidentModal] = useState<boolean>(false);
  const [openResiEditModal, setOpenResiEditModal] = useState<boolean>(false);
  const [editResiData, setEditResiData] = useState<ResidentProfile | null>(null);
  const [deleteResidentModal, setDeleteResidentModal] = useState<boolean>(false);
  const [showArchivedResidents, setShowArchivedResidents] = useState<boolean>(false);
  const [restoringResidentId, setRestoringResidentId] = useState<string>("");
  const dailyActionsMenuRef = useRef<Menu>(null);
  const [dailyActionsResident, setDailyActionsResident] = useState<ResidentProfile | null>(null);

  const [linkedUsers, setLinkedUsers] = useState<UserProfile[] | null>(null);
  const [linkedLookupFlag, setLinkedLookupFlag] = useState<boolean>(false);
  const [openLinkedLookupModal, setOpenLinkedLookupModal] = useState<boolean>(false);
  const [linkedUsersLoading, setLinkedUsersLoading] = useState<boolean>(false);

  const userRoleType = authStore.getUserRoleType();
  const [userGroups, setUserGroups] = useState<string[]>([]);
  const isAdminUser = userGroups.includes(USER_GROUP_ADMIN) || userRoleType === ROLE_TYPE_ADMIN;
  const [copied, setCopied] = useState<boolean>(false);
  const [copyUrl, setCopyUrl] = useState<string>("");
  const [websiteInquiryLoading, setWebsiteInquiryLoading] = useState<boolean>(false);
  const [websiteInquiryFlag, setWebsiteInquiryFlag] = useState<boolean>(false);
  const [websiteInquiries, setWebsiteInquiries] = useState<WebsiteInquiry[]>([]);
  const [openWebsiteInquiriesModal, setOpenWebsiteInquiriesModal] = useState<boolean>(false);
  const [openWebsiteStatisticsModal, setOpenWebsiteStatisticsModal] = useState<boolean>(false);

  const [disableLoading, setDisableLoading] = useState<boolean>(false);
  const [publishLoading, setPublishLoading] = useState<boolean>(false);
  const [showWebsitePublishLimitDialog, setShowWebsitePublishLimitDialog] = useState<boolean>(false);
  const [websitePublishLimitDialogMessage, setWebsitePublishLimitDialogMessage] = useState<string>("An active subscription is required to publish your website.");
  const [showWebsiteTemplateLimitDialog, setShowWebsiteTemplateLimitDialog] = useState<boolean>(false);
  const [websiteTemplateLimitDialogMessage, setWebsiteTemplateLimitDialogMessage] = useState<string>("Choose a premium website template by unlocking template access for this home, or subscribe for template access across all homes.");
  const [websiteTemplateCreditsAvailable, setWebsiteTemplateCreditsAvailable] = useState<number>(0);
  const [websiteTemplateUnlockedHomeIds, setWebsiteTemplateUnlockedHomeIds] = useState<string[]>([]);
  const [billingSubscriptionActive, setBillingSubscriptionActive] = useState<boolean>(false);
  const [updatingWebsiteTemplate, setUpdatingWebsiteTemplate] = useState<string | null>(null);

  const [openEditHiringProfileModal, setOpenEditHiringProfileModal] = useState<boolean>(false);
  const [updateHiringProfileFlag, setUpdateHiringProfileFlag] = useState<boolean>(false);
  const [caregiverHiringProfileData, setCaregiverHiringProfileData] = useState<HomeHiringJobProfile | undefined>(undefined);
  const [hiringProfileLoader, setHiringProfileLoading] = useState<boolean>(false);

  const [clientAvailabilityLoader, setClientAvailabilityLoader] = useState<boolean>(false);
  const [clientAvailabilityProfileData, setClientAvailabilityProfileData] = useState<ClientAvailabilityProfile | undefined>(undefined);
  const [updateClientAvailabilityFlag, setUpdateClientAvailabilityFlag] = useState<boolean>(false);
  const [openEditClientAvailabilityModal, setOpenEditClientAvailabilityModal] = useState<boolean>(false);

  const [openUserFeedbackModal, setOpenUserFeedbackModal] = useState<boolean>(false);
  const [feedbckToUserId, setFeedbckToUserId] = useState<string | undefined>(undefined);

  const [openWebsiteUrlEditorModal, setOpenWebsiteUrlEditorModal] = useState<boolean>(false);
  const [newWebsiteUrl, setNewWebsiteUrl] = useState<string>("");
  const [newWebsiteUrlUpdateError, setNewWebsiteUrlUpdateError] = useState<string>("");
  const [updatingWebsiteUrl, setUpdatingWebsiteUrl] = useState<boolean>(false);
  const [updatingWebsiteUrlSuccess, setUpdatingWebsiteUrlSuccess] = useState<boolean>(false);

  const [websiteQRCodeUrl, setWebsiteQRCodeUrl] = useState<string>("");

  useEffect(() => {
    const fetchData = async () => {
      setProfileLoading(true);
      try {
        await backendAPIStore.init();
        setUserGroups(authStore.getUserGroups());

        // get home details
        const response = await backendAPIStore.getBusinessHomeDetails(homeId);
        setBusinessData(response);
        const url = getWebsiteUrlForHome(response);
        setCopyUrl(url);
        if (response?.isWebsiteActive) {
          const qrCodeUrl = await getQrCodeDataUrl(url);
          setWebsiteQRCodeUrl(qrCodeUrl);
        }
      } catch (error: any) {
        toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
      } finally {
        setProfileLoading(false);
      }
    };
    fetchData();
  }, [homeId, flag, openAddResidentModal, updatingWebsiteUrlSuccess]);

  useEffect(() => {
    const fetchCaregiverHiringProfileDetails = async () => {
      if (!homeId) {
        return;
      }
      setHiringProfileLoading(true);
      try {
        await backendAPIStore.init();
        const response = await backendAPIStore.getHomeHiringJobProfile(homeId);
        if (response) {
          setCaregiverHiringProfileData(response);
        }
      } catch (error: any) {
        toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
      } finally {
        setHiringProfileLoading(false);
      }
    };
    fetchCaregiverHiringProfileDetails();
  }, [homeId, updateHiringProfileFlag]);

  useEffect(() => {
    const fetchBillingSnapshot = async () => {
      const userId = authStore.getUserId();
      if (!userId) {
        return;
      }
      try {
        await backendAPIStore.init();
        const billingDetails = await backendAPIStore.getSubscription(userId);
        setBillingSubscriptionActive(!!billingDetails?.isSubscribed);
        setWebsiteTemplateCreditsAvailable(Number(billingDetails?.websiteTemplateCreditsAvailable || 0));
        setWebsiteTemplateUnlockedHomeIds(Array.isArray(billingDetails?.websiteTemplateUnlockedHomeIds) ? billingDetails.websiteTemplateUnlockedHomeIds : []);
      } catch (error) {
        console.error("Unable to load website template billing details", error);
      }
    };

    fetchBillingSnapshot();
  }, [authStore, backendAPIStore, flag, homeId, updatingWebsiteUrlSuccess]);

  useEffect(() => {
    const fetchData = async () => {
      setResidentsLoading(true);
      try {
        await backendAPIStore.init();
        const response = await backendAPIStore.getResidentsForHome(homeId);
        setResidents(response);
      } catch (error: any) {
        toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
      } finally {
        setResidentsLoading(false);
      }
    };
    fetchData();
  }, [homeId, residentFlag, deleteResidentModal, openAddResidentModal]);

  useEffect(() => {
    const fetchData = async () => {
      setLinkedUsersLoading(true);
      try {
        await backendAPIStore.init();
        const response = await backendAPIStore.getLinkedUsersForHome(homeId);
        setLinkedUsers(response);
      } catch (error: any) {
        toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
      } finally {
        setLinkedUsersLoading(false);
      }
    };
    fetchData();
  }, [homeId, linkedLookupFlag]);

  useEffect(() => {
    const fetchWebsiteInquiries = async () => {
      if (!homeId) {
        return;
      }
      setWebsiteInquiryLoading(true);
      try {
        await backendAPIStore.init();
        const response = await backendAPIStore.listWebsiteInquiries(homeId, null);
        if (response) {
          setWebsiteInquiries(response.inquiries);
        }
      } catch (error: any) {
        toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
      } finally {
        setWebsiteInquiryLoading(false);
      }
    };
    fetchWebsiteInquiries();
  }, [homeId, websiteInquiryFlag]);

  useEffect(() => {
    const fetchClientAvailabilityProfileDetails = async () => {
      if (!homeId) {
        return;
      }
      setClientAvailabilityLoader(true);
      try {
        await backendAPIStore.init();
        const response = await backendAPIStore.getClientAvailabilityProfile(homeId);
        if (response) {
          setClientAvailabilityProfileData(response);
        }
      } catch (error: any) {
        toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
      } finally {
        setClientAvailabilityLoader(false);
      }
    };
    fetchClientAvailabilityProfileDetails();
  }, [homeId, updateClientAvailabilityFlag]);

  const isResidentActive = (resident: ResidentProfile | null | undefined) => {
    const value: any = resident?.active;
    if (typeof value === "string") return value.trim().toLowerCase() !== "false";
    return value !== false && value !== 0;
  };

  const activeResidentCount = Array.isArray(residents)
    ? residents.filter((resident) => isResidentActive(resident)).length
    : 0;

  const archivedResidentCount = Array.isArray(residents)
    ? residents.filter((resident) => !isResidentActive(resident)).length
    : 0;

  const residentTable = Array.isArray(residents)
    ? residents
      .filter((resident) => showArchivedResidents ? !isResidentActive(resident) : isResidentActive(resident))
      .map((resident) => ({
      data: resident,
      name: `${resident.firstName} ${resident.lastName}`,
    }))
    : [];

  const archivedResidentPath = (path: string) => `${path}${path.includes("?") ? "&" : "?"}archived=true`;

  const restoreArchivedResident = async (resident: ResidentProfile) => {
    if (!resident?.residentId) return;
    setRestoringResidentId(resident.residentId);
    try {
      await backendAPIStore.init();
      await backendAPIStore.updateResident({ ...resident, active: true });
      setResidentFlag((previousValue) => !previousValue);
      toast.current?.show({ severity: "success", summary: "Resident Activated", detail: "The resident is active again.", life: 3000 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to activate resident.", life: 4000 });
    } finally {
      setRestoringResidentId("");
    }
  };

  const dailyActionItems = dailyActionsResident ? [
    { label: "ADL", icon: "pi pi-check-square", command: () => navigate(archivedResidentPath(RouterAddress.getProviderResidentAdlPagePath(String(dailyActionsResident.residentId || ""), homeId!))) },
    { label: "Daily Notes", icon: "pi pi-file-edit", command: () => navigate(archivedResidentPath(RouterAddress.getProviderResidentDailyNotesPagePath(String(dailyActionsResident.residentId || ""), undefined, homeId))) },
    { label: "Incident Reports", icon: "pi pi-exclamation-circle", command: () => navigate(archivedResidentPath(RouterAddress.getProviderResidentIncidentReportPagePath(String(dailyActionsResident.residentId || ""), undefined, homeId))) },
    { label: "Behavior Tracker", icon: "pi pi-chart-line", command: () => navigate(archivedResidentPath(RouterAddress.getProviderResidentDailyBehaviorLogPagePath(String(dailyActionsResident.residentId || ""), undefined, homeId))) },
  ] : [];

  const linkedUsersTable = Array.isArray(linkedUsers)
    ? linkedUsers.map((user) => ({
      data: user,
      name: `${user.firstName} ${user.lastName}`,
    }))
    : [];

  const websiteTemplateItems = WEBSITE_TEMPLATE_OPTIONS.map((option) => {
    const currentDraftTemplateKey = businessData?.websiteDraftTemplateKey || DEFAULT_WEBSITE_TEMPLATE_KEY;
    const currentPublishedTemplateKey = businessData?.websitePublishedTemplateKey || DEFAULT_WEBSITE_TEMPLATE_KEY;
    const isDraft = currentDraftTemplateKey === option.key;
    const isLive = currentPublishedTemplateKey === option.key;
    const requiresPremiumAccess = option.key !== DEFAULT_WEBSITE_TEMPLATE_KEY;
    const hasTemplateAccessForHome = billingSubscriptionActive || websiteTemplateUnlockedHomeIds.includes(homeId || "");

    return {
      ...option,
      isDraft,
      isLive,
      requiresPremiumAccess,
      hasTemplateAccessForHome,
    };
  });

  const handleUnlinkUser = async (data) => {
    confirmDialog({
      message: renderConfirmMessage(
        "Remove linked caregiver?",
        `${data.firstName} ${data.lastName} will no longer be connected to this home.`,
      ),
      header: 'Remove Confirmation',
      icon: 'pi pi-info-circle',
      ...appConfirmDialogClasses,
      accept: async () => {
        const response = await backendAPIStore.deleteHomeLink(data.userId, homeId);
        if (response) {
          setLinkedLookupFlag(!linkedLookupFlag);
        }
      },
      reject: () => { }
    });
  };

  const residentProfileItemTemplate = (rowData: any) => {
    return (
      <div className="grid align-items-center">
        <div className="col-12 md:col-5">
          <div className="flex align-items-center gap-3">
            <div
              style={{
                width: "2.75rem",
                height: "2.75rem",
                borderRadius: "999px",
                background: "#e0f2fe",
                color: "#185DA2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
              }}
            >
              {rowData.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={{ textTransform: "capitalize", fontWeight: 700, color: "#0f172a" }}>{rowData.name}</div>
              <div style={appMutedTextStyle}>
                Resident
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 md:col-3">
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              borderRadius: "999px",
              padding: "0.45rem 0.85rem",
              background: rowData.data.active ? "#ecfdf3" : "#fef2f2",
              color: rowData.data.active ? "#047857" : "#b91c1c",
              fontWeight: 700,
            }}
          >
            {rowData.data.active ? "Active" : "Archived"}
          </span>
        </div>
        <div className="col-12 md:col-4 flex md:justify-content-end flex-wrap">
          {!isResidentActive(rowData.data) ? (
            <Button icon="pi pi-refresh" className="p-button-outlined m-2" rounded label="Activate" severity="success"
              loading={restoringResidentId === rowData.data.residentId}
              disabled={Boolean(restoringResidentId)}
              onClick={() => { void restoreArchivedResident(rowData.data); }}
            />
          ) : null}
          <Button icon="pi pi-user" className="p-button-outlined m-2" rounded label="Profile" severity="warning" data-tour="home-open-resident-profile"
            onClick={() => {
              navigate(RouterAddress.getResidentDetailsPagePath(homeId!, rowData.data.residentId));
            }}
          />
          <Button icon="pi pi-sparkles" className="p-button-outlined m-2" rounded label="NCP" severity="warning"
            onClick={() => {
              navigate(RouterAddress.getResidentNCPPagePath(homeId!, rowData.data.residentId));
            }}
          />
          {!isResidentActive(rowData.data) ? (
            <>
              <Button icon="pi pi-list" className="p-button-outlined m-2" rounded label="Daily Actions" severity="warning"
                onClick={(event) => {
                  setDailyActionsResident(rowData.data);
                  dailyActionsMenuRef.current?.toggle(event);
                }}
              />
              <Menu ref={dailyActionsMenuRef} model={dailyActionItems} popup />
            </>
          ) : SHOW_ADL_ENTRY_POINTS ? (
            <Button icon="pi pi-check-square" className="p-button-outlined m-2" rounded label="ADL" severity="warning"
              onClick={() => {
                navigate(RouterAddress.getProviderResidentAdlPagePath(rowData.data.residentId, homeId!));
              }}
            />
          ) : null}
        </div>
      </div>
    );
  }

  const linkedUserProfileItemTemplate = (rowData: any) => {
    return (
      <div className="grid align-items-center">
        <div className="col-12 md:col-4">
          <div className="flex align-items-center gap-3">
            <div
              style={{
                width: "2.75rem",
                height: "2.75rem",
                borderRadius: "999px",
                background: "#fff7ed",
                color: "#b45309",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
              }}
            >
              {rowData.data.userProfile.firstName?.charAt(0)?.toUpperCase() || "C"}
            </div>
            <div style={{ textTransform: "capitalize", fontWeight: 700, color: "#0f172a" }}>
              {rowData.data.userProfile.firstName} {rowData.data.userProfile.lastName}
            </div>
          </div>
        </div>
        <div className="col-12 md:col-5">
          <div>Email: <a href={`mailto:${rowData.data.userProfile.email}`}>{rowData.data.userProfile.email}</a></div>
          <div>Phone: <a href={`tel:${rowData.data.userProfile.phoneNumber}`}>{rowData.data.userProfile.phoneNumber}</a></div>
        </div>
        <div className="col-12 md:col-3 flex md:justify-content-end">
          {/* <Button icon="pi pi-comments" className="p-button-outlined m-1" rounded label="Review" severity="warning"
            onClick={(e) => {
              e.preventDefault();
              setFeedbckToUserId(rowData.data.userId);
              setOpenUserFeedbackModal(true);
            }} /> */}
          <Button icon="pi pi-link" className="p-button-outlined m-1" rounded label="Remove" severity="danger"
            onClick={(e) => { e.preventDefault(); handleUnlinkUser(rowData.data.userProfile) }} />
        </div>
      </div>
    );
  }

  const residentHeaderTemplate = (
    renderSurfaceHeader(
      `Resident List (${residentTable.length})`,
      "Review current residents and jump into each resident profile.",
      "pi pi-users",
    )
  );

  const linkedUsersHeaderTemplate = (
    renderSurfaceHeader(
      `Linked Caregivers (${linkedUsersTable.length})`,
      "Keep caregiver connections organized and remove access when needed.",
      "pi pi-link",
    )
  );

  const handleCopy = () => {
    if (!copyUrl) return;
    if (
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === "function"
    ) {
      navigator.clipboard
        .writeText(copyUrl)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch((err) => console.error("Clipboard write failed:", err));
    } else {
      const tempInput = document.createElement("input");
      tempInput.value = copyUrl;
      document.body.appendChild(tempInput);
      tempInput.select();
      document.execCommand("copy");
      document.body.removeChild(tempInput);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleStatisticsClick = () => {
    setOpenWebsiteStatisticsModal(true);
  }

  const handleInquiriesClick = () => {
    setOpenWebsiteInquiriesModal(true);
  }

  const handleEditWebsiteClick = () => {
    if (isStepActive("website-open-editor")) {
      completeStep("website-open-editor");
      navigate(RouterAddress.getManageWebsitePath(homeId!));
      return;
    }
    openUrlInCurrentTab(RouterAddress.getManageWebsitePath(homeId!));
  }

  const handleTemplatePreviewClick = (templateKey: string) => {
    openUrlInNewTab(RouterAddress.getManageWebsitePreviewPath(homeId!, templateKey));
  };

  const handlePublish = async (options?: { suppressReload?: boolean; suppressSuccessToast?: boolean }) => {
    const data = {
      homeId: homeId,
      type: "PUBLISH",
    };
    try {
      setPublishLoading(true);
      await backendAPIStore.init();
      const response = await backendAPIStore.publishTemplate(data);
      if (response) {
        await backendAPIStore.enableDisableTemplate(businessData?.homeId, true);
      }
      if (!options?.suppressSuccessToast) {
        toast.current?.show({ severity: "success", summary: "Website published", detail: "Your website is now live with the latest template and content.", life: 3000 });
      }
      if (!options?.suppressReload) {
        window.location.reload();
      }
      return true;
    } catch (error) {
      console.error("Error fetching business home details:", error);
      if ((error as any)?.code === "WEBSITE_TEMPLATE_LIMIT_REACHED" || (error as any)?.cause?.message === "WEBSITE_TEMPLATE_LIMIT_REACHED") {
        const availableCredits = Number((error as any)?.details?.websiteTemplateCreditsAvailable || (error as any)?.websiteTemplateCreditsAvailable || 0);
        setWebsiteTemplateLimitDialogMessage(
          availableCredits > 0
            ? `You have ${availableCredits} unused website template credit${availableCredits === 1 ? "" : "s"} on your account. Use one to publish this home with a premium template.`
            : "Subscribe for template access across all homes, or buy website template access to unlock premium templates for this home."
        );
        setShowWebsiteTemplateLimitDialog(true);
      } else if ((error as any)?.code === "SUBSCRIPTION_LIMIT_REACHED" || (error as any)?.cause?.message === "SUBSCRIPTION_LIMIT_REACHED") {
        setWebsitePublishLimitDialogMessage("An active subscription is required to publish your website.");
        setShowWebsitePublishLimitDialog(true);
      } else {
        toast.current?.show(getToastMessageForSubscriptionLimitReached(error));
      }
      return false;
    } finally {
      setPublishLoading(false);
    }
  };

  const handleTemplateSelection = async (templateKey: string) => {
    try {
      setUpdatingWebsiteTemplate(templateKey);
      await backendAPIStore.init();
      await backendAPIStore.updateWebsiteTemplateSelection({
        homeId: homeId!,
        templateKey,
      });
      const published = await handlePublish({ suppressReload: true, suppressSuccessToast: true });
      setFlag((prev) => !prev);
      toast.current?.show({
        severity: "success",
        summary: published ? "Template selected and published" : "Template selected",
        detail: published
          ? "Your website is now live with the selected template."
          : "Your draft website template is ready to preview and publish.",
        life: 3000
      });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to update template selection.", life: 3000 });
    } finally {
      setUpdatingWebsiteTemplate(null);
    }
  };

  const disableWebsite = async (flag: boolean | undefined) => {
    if (flag === undefined) return;
    confirmDialog({
      message: renderConfirmMessage(
        flag ? "Bring the website back online?" : "Take the website offline?",
        flag
          ? "Your public home website will become visible to visitors again."
          : "Visitors will no longer be able to access the public website until you reactivate it.",
      ),
      header: flag ? 'Activate Confirmation' : 'Deactivate Confirmation',
      icon: 'pi pi-info-circle',
      ...appConfirmDialogClasses,
      accept: async () => {
        await backendAPIStore.init();
        await backendAPIStore.enableDisableTemplate(
          businessData?.homeId,
          flag,
        );
        setDisableLoading(false);
        window.location.reload();
      },
      reject: () => { }
    });
  };

  const facilityProfileHeaderTemplate = (
    renderSurfaceHeader(
      authExperience.providerHome.profileSectionTitle,
      authExperience.providerHome.profileSectionSubtitle,
      "pi pi-building",
    )
  );

  const websiteHeaderTemplate = (
    renderSurfaceHeader(
      authExperience.providerHome.websiteSectionTitle,
      authExperience.providerHome.websiteSectionSubtitle,
      "pi pi-globe",
    )
  );

  const hiringProfileHeaderTemplate = (
    <div className="flex flex-wrap align-items-center justify-content-between gap-2">
      <span className="text-xl text-900">Manage caregiver hiring profiles</span>
    </div>
  );

  const clientAvailabilityHeaderTemplate = (
    <div className="flex flex-wrap align-items-center justify-content-between gap-2">
      <span className="text-xl text-900">Manage your listings</span>
    </div>
  );

  useEffect(() => {
    if (currentStepId === "website-open-editor") {
      setActiveAccordionIndex([0, 1]);
    }
  }, [currentStepId]);

  if (userRoleType && userRoleType !== ROLE_TYPE_PROVIDER) {
    return <Unauthorized />;
  }

  const onAddResidentClick = () => {
    setOpenAddResidentModal(true);
  }

  const onAddCaregiverClick = () => {
    setOpenLinkedLookupModal(true);
  }

  const onUpdateWebsiteUrlClick = async () => {
    const sanitizedWebsiteSlug = sanitizeWebsiteSlug(newWebsiteUrl);

    if (!sanitizedWebsiteSlug) {
      toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: 'URL is required', life: 3000 });
      return;
    }

    setUpdatingWebsiteUrl(true);

    try {
      await backendAPIStore.updateWebsiteUrl({
        homeId: homeId!,
        newUrl: sanitizedWebsiteSlug,
      });
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Website URL updated', life: 3000 });
      setOpenWebsiteUrlEditorModal(false);
      setUpdatingWebsiteUrlSuccess(!updatingWebsiteUrlSuccess);
    } catch (error: any) {
      setNewWebsiteUrlUpdateError(error.message);
    } finally {
      setUpdatingWebsiteUrl(false);
    }
  };

  const onDeleteHome = () => {
    confirmDialog({
      message: renderConfirmMessage(
        "Delete this home permanently?",
        "This cannot be undone. The home will be marked inactive and its website will go offline. Move or archive all residents before deleting the home.",
      ),
      header: 'Delete Confirmation',
      icon: 'pi pi-info-circle',
      ...appConfirmDialogClasses,
      accept: async () => {
        try {
          await backendAPIStore.init();
          await backendAPIStore.deleteHome(homeId!);
          toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Home deleted', life: 3000 });
          navigate(RouterAddress.PROVIDER_HOME);
        } catch (error: any) {
          toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
        }
      },
      reject: () => { }
    });
  }

  return (
    <div style={{ padding: mobile ? "1rem" : "1.25rem" }}>
      <Toast ref={toast} />
      <SubscribeForUnlimitedAccessDialog
        visible={showWebsitePublishLimitDialog}
        onHide={() => setShowWebsitePublishLimitDialog(false)}
        title="More Website Publish Access Needed"
        message={websitePublishLimitDialogMessage}
        billingPath={RouterAddress.BILLING}
        billingButtonLabel="Subscription"
      />
      <SubscribeForUnlimitedAccessDialog
        visible={showWebsiteTemplateLimitDialog}
        onHide={() => setShowWebsiteTemplateLimitDialog(false)}
        title="Website Template Access Needed"
        message={websiteTemplateLimitDialogMessage}
        billingPath={`${RouterAddress.BILLING}?tab=one-time`}
      />

      <div style={{ ...dashboardPageContainerStyle, display: "flex", flexDirection: "column", gap: "1.25rem" }} className="p-component">
        {renderDashboardHeader(
          businessData?.businessName || authExperience.providerHome.detailsTitle,
          authExperience.providerHome.detailsSubtitle,
        )}

        <Accordion
          multiple
          activeIndex={activeAccordionIndex}
          onTabChange={(e) => setActiveAccordionIndex(Array.isArray(e.index) ? e.index : [e.index])}
          className="flex flex-column gap-3"
        >
          <AccordionTab headerTemplate={facilityProfileHeaderTemplate}>
            {profileLoading ? (
              <div className="col-12 text-center d-flex justify-content-center align-items-center">
                <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
              </div>
            ) : (
              <HomeProfile
                homeData={businessData}
                onEditClick={() => {
                  setOpenEditModal(true);
                  setEditData(businessData);
                }}
                onDeleteClick={isAdminUser ? onDeleteHome : undefined}
              />
            )}
          </AccordionTab>
          <AccordionTab headerTemplate={websiteHeaderTemplate}>
            {profileLoading ? (
              <div className="col-12 text-center d-flex justify-content-center align-items-center">
                <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
              </div>
            ) : (
              <div className="grid">
                <div className="col-12 lg:col-7">
                  <div style={{ ...dashboardSectionCardStyle, padding: "1.25rem", background: "#ffffff" }} className="h-full">
                    <div className="grid">
                      <div className="col-12 md:col-6">
                        <div style={{ fontWeight: 700, color: "#0f172a" }}>Website Status</div>
                        <div
                          className="mt-3"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            borderRadius: "999px",
                            padding: "0.55rem 0.95rem",
                            background: businessData?.isWebsiteActive ? "#ecfdf3" : "#fef2f2",
                            color: businessData?.isWebsiteActive ? "#047857" : "#b91c1c",
                            fontWeight: 700,
                          }}
                        >
                          <i className={`pi ${businessData?.isWebsiteActive ? "pi-check-circle" : "pi-times-circle"}`} />
                          <span>{businessData?.isWebsiteActive ? "Live" : "Offline"}</span>
                        </div>
                      </div>
                      <div className="col-12 md:col-6">
                        <div style={{ fontWeight: 700, color: "#0f172a" }}>Public URL</div>
                        {businessData?.isWebsiteActive ? (
                          <div className="mt-3 flex flex-wrap align-items-center gap-2">
                            <a target="_blank" rel="noopener noreferrer" href={copyUrl}>{copyUrl}</a>
                            <Button icon="pi pi-copy" outlined rounded severity="warning" tooltip="Copy URL" onClick={handleCopy} />
                            <Button severity="warning" outlined rounded label="Change URL" onClick={() => setOpenWebsiteUrlEditorModal(true)} />
                            {copied ? <span style={{ color: "#15803d", fontWeight: 600 }}>Copied</span> : null}
                          </div>
                        ) : (
                          <div className="mt-3" style={appMutedTextStyle}>
                            Publish the site to generate and share a public URL.
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="grid mt-3">
                      <div className="col-12 md:col-6">
                        <div style={{ padding: "0.25rem 0" }}>
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>How to manage the site</div>
                          <div className="mt-3 flex flex-column gap-2" style={appMutedTextStyle}>
                            <div><strong style={{ color: "#0f172a" }}>Open Editor:</strong> update sections and preview changes.</div>
                            <div><strong style={{ color: "#0f172a" }}>Publish Site:</strong> push your latest edits live when you have an active subscription.</div>
                            <div><strong style={{ color: "#0f172a" }}>View Stats:</strong> review traffic and engagement.</div>
                          </div>
                        </div>
                      </div>
                      <div className="col-12 md:col-6">
                        <div style={{ padding: "0.25rem 0" }}>
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>Quick actions</div>
                          <div className="mt-3 flex flex-wrap gap-2">
                            <Button label="Open Editor" className="p-button-outlined" severity="warning" rounded data-tour="home-website-open-editor" onClick={handleEditWebsiteClick} />
                            <Button
                              label="Publish Site"
                              className="p-button-outlined"
                              severity="warning"
                              rounded
                              onClick={() => {
                                if (!billingSubscriptionActive) {
                                  setWebsitePublishLimitDialogMessage("An active subscription is required to publish your website.");
                                  setShowWebsitePublishLimitDialog(true);
                                  return;
                                }
                                void handlePublish();
                              }}
                              loading={publishLoading}
                            />
                            {businessData?.isWebsiteActive ? (
                              <>
                                <Button label="View Inquiries" className="p-button-outlined" severity="warning" rounded onClick={handleInquiriesClick} />
                                <Button label="View Stats" className="p-button-outlined" severity="warning" rounded onClick={handleStatisticsClick} />
                                <Button label="Take Offline" className="p-button-outlined" severity="danger" onClick={() => disableWebsite(false)} rounded loading={disableLoading} />
                              </>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-12 lg:col-5">
                  <div style={{ display: "grid", gap: "1rem" }}>
                    <div style={{ ...dashboardSectionCardStyle, padding: "1rem", background: "#ffffff" }}>
                      <div className="flex flex-wrap align-items-start justify-content-between gap-2">
                        <div>
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>Choose Website Template</div>
                          <div className="mt-1" style={{ color: "#64748b", lineHeight: 1.6, fontSize: "0.92rem" }}>
                            Pick a draft, preview it, then publish when ready.
                          </div>
                        </div>
                        <div
                          style={{
                            background: "#f8fafc",
                            border: "1px solid #e2e8f0",
                            borderRadius: "999px",
                            padding: "0.45rem 0.8rem",
                            color: "#475569",
                            fontSize: "0.84rem",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Credits: <strong>{websiteTemplateCreditsAvailable}</strong>
                        </div>
                      </div>
                      <div className="mt-3">
                        <DataView
                          value={websiteTemplateItems}
                          layout="list"
                          itemTemplate={(option: any) => (
                            <div
                              key={option.key}
                              style={{
                                border: `1px solid ${option.isDraft ? option.accentColor : "#e2e8f0"}`,
                                borderRadius: "14px",
                                padding: "0.8rem 0.9rem",
                                background: option.isDraft ? `${option.accentColor}12` : "#ffffff",
                              }}
                              className="mb-2"
                            >
                              <div className="flex flex-column lg:flex-row lg:align-items-center lg:justify-content-between gap-3">
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div
                                    style={{
                                      borderRadius: "16px",
                                      overflow: "hidden",
                                      border: `1px solid ${option.isDraft ? option.accentColor : "#e2e8f0"}`,
                                      background: option.previewBackground,
                                      minHeight: "108px",
                                      padding: "0.8rem",
                                      marginBottom: "0.8rem",
                                    }}
                                  >
                                    {option.key === "template-1" ? (
                                      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 0.8fr", gap: "0.65rem", height: "100%" }}>
                                        <div style={{ background: "rgba(15,108,189,0.18)", borderRadius: "12px", padding: "0.7rem", display: "grid", alignContent: "space-between" }}>
                                          <div style={{ width: "44%", height: "0.55rem", borderRadius: "999px", background: option.accentColor }} />
                                          <div style={{ display: "grid", gap: "0.35rem" }}>
                                            <div style={{ width: "78%", height: "0.65rem", borderRadius: "999px", background: "#0f172a" }} />
                                            <div style={{ width: "92%", height: "0.45rem", borderRadius: "999px", background: "rgba(15,23,42,0.34)" }} />
                                            <div style={{ width: "68%", height: "0.45rem", borderRadius: "999px", background: option.previewAccent }} />
                                          </div>
                                        </div>
                                        <div style={{ display: "grid", gap: "0.55rem" }}>
                                          <div style={{ background: "#ffffff", borderRadius: "12px", minHeight: "3.3rem" }} />
                                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.55rem" }}>
                                            <div style={{ background: option.previewSurface, borderRadius: "12px", minHeight: "2.6rem" }} />
                                            <div style={{ background: "#ffffff", borderRadius: "12px", minHeight: "2.6rem" }} />
                                          </div>
                                        </div>
                                      </div>
                                    ) : option.key === "template-2" ? (
                                      <div style={{ display: "grid", gap: "0.55rem", height: "100%" }}>
                                        <div style={{ background: `linear-gradient(135deg, ${option.accentColor} 0%, ${option.previewAccent} 100%)`, borderRadius: "14px", minHeight: "2.75rem" }} />
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.55rem", flex: 1 }}>
                                          <div style={{ background: "#ffffff", borderRadius: "12px", minHeight: "4rem" }} />
                                          <div style={{ display: "grid", gap: "0.55rem" }}>
                                            <div style={{ background: option.previewSurface, borderRadius: "12px", minHeight: "1.7rem" }} />
                                            <div style={{ background: "#ffffff", borderRadius: "12px", minHeight: "1.7rem" }} />
                                          </div>
                                        </div>
                                      </div>
                                    ) : option.key === "template-3" ? (
                                      <div style={{ display: "grid", gridTemplateColumns: "0.8fr 1.2fr", gap: "0.55rem", height: "100%" }}>
                                        <div style={{ background: "#ffffff", borderRadius: "14px", padding: "0.65rem", display: "grid", gap: "0.45rem" }}>
                                          <div style={{ width: "62%", height: "0.6rem", borderRadius: "999px", background: option.accentColor }} />
                                          <div style={{ background: option.previewSurface, borderRadius: "10px", minHeight: "1.5rem" }} />
                                          <div style={{ background: option.previewSurface, borderRadius: "10px", minHeight: "1.5rem" }} />
                                        </div>
                                        <div style={{ background: `linear-gradient(135deg, ${option.accentColor} 0%, ${option.previewAccent} 100%)`, borderRadius: "14px", minHeight: "100%", position: "relative", overflow: "hidden" }}>
                                          <div style={{ position: "absolute", left: "0.7rem", bottom: "0.7rem", right: "2.3rem", height: "0.65rem", borderRadius: "999px", background: "rgba(255,255,255,0.88)" }} />
                                        </div>
                                      </div>
                                    ) : option.key === "template-4" ? (
                                      <div style={{ display: "grid", gap: "0.55rem", height: "100%" }}>
                                        <div style={{ display: "grid", gridTemplateColumns: "1.25fr 0.75fr", gap: "0.55rem", flex: 1 }}>
                                          <div style={{ background: "#ffffff", borderRadius: "14px", minHeight: "4.25rem" }} />
                                          <div style={{ display: "grid", gap: "0.55rem" }}>
                                            <div style={{ background: option.previewSurface, borderRadius: "12px", minHeight: "1.85rem" }} />
                                            <div style={{ background: "#ffffff", borderRadius: "12px", minHeight: "1.85rem" }} />
                                          </div>
                                        </div>
                                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.55rem" }}>
                                          <div style={{ background: option.previewAccent, borderRadius: "12px", minHeight: "1.85rem" }} />
                                          <div style={{ background: "#ffffff", borderRadius: "12px", minHeight: "1.85rem" }} />
                                          <div style={{ background: option.previewSurface, borderRadius: "12px", minHeight: "1.85rem" }} />
                                        </div>
                                      </div>
                                    ) : (
                                      <div style={{ display: "grid", gridTemplateColumns: "0.9fr 1.1fr", gap: "0.55rem", height: "100%" }}>
                                        <div style={{ background: "#ffffff", borderRadius: "14px", padding: "0.75rem", display: "grid", alignContent: "space-between" }}>
                                          <div style={{ width: "34%", height: "0.55rem", borderRadius: "999px", background: option.accentColor }} />
                                          <div style={{ display: "grid", gap: "0.35rem" }}>
                                            <div style={{ width: "82%", height: "0.65rem", borderRadius: "999px", background: "#0f172a" }} />
                                            <div style={{ width: "60%", height: "0.5rem", borderRadius: "999px", background: option.previewAccent }} />
                                          </div>
                                        </div>
                                        <div style={{ background: "linear-gradient(180deg, rgba(214,31,105,0.15) 0%, rgba(74,6,36,0.85) 100%)", borderRadius: "14px", position: "relative", overflow: "hidden" }}>
                                          <div style={{ position: "absolute", left: "0.7rem", right: "0.7rem", bottom: "0.7rem", height: "0.6rem", borderRadius: "999px", background: "rgba(255,255,255,0.86)" }} />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex flex-wrap align-items-center gap-2">
                                    <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.96rem" }}>{option.name}</div>
                                    {option.isLive && <span style={{ background: "#ecfdf3", color: "#047857", borderRadius: "999px", padding: "0.2rem 0.55rem", fontSize: "0.74rem", fontWeight: 700 }}>Live</span>}
                                    {option.isDraft && <span style={{ background: "#eff6ff", color: "#185DA2", borderRadius: "999px", padding: "0.2rem 0.55rem", fontSize: "0.74rem", fontWeight: 700 }}>Selected</span>}
                                    {option.requiresPremiumAccess && !option.hasTemplateAccessForHome && (
                                      <span style={{ background: "#fff7ed", color: "#b45309", borderRadius: "999px", padding: "0.2rem 0.55rem", fontSize: "0.74rem", fontWeight: 700 }}>
                                        Premium
                                      </span>
                                    )}
                                  </div>
                                  <div className="mt-1" style={{ color: "#64748b", lineHeight: 1.5, fontSize: "0.85rem" }}>
                                    {option.description}
                                  </div>
                                  <div className="mt-2" style={{ color: option.accentColor, fontWeight: 700, fontSize: "0.8rem" }}>
                                    {option.layoutLabel}
                                  </div>
                                </div>
                                <div className="flex flex-wrap gap-2 lg:justify-content-end">
                                  <Button label="Preview" rounded outlined size="small" onClick={() => handleTemplatePreviewClick(option.key)} />
                                  <Button
                                    label={option.isDraft ? "Selected" : "Use"}
                                    rounded
                                    severity="warning"
                                    size="small"
                                    disabled={option.isDraft}
                                    loading={updatingWebsiteTemplate === option.key}
                                    onClick={() => handleTemplateSelection(option.key)}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        />
                      </div>
                    </div>
                    <div style={{ ...dashboardSectionCardStyle, padding: "1.25rem", background: "#ffffff", height: "100%" }} className="flex flex-column align-items-center justify-content-center gap-3 text-center">
                      {businessData?.isWebsiteActive && websiteQRCodeUrl ? (
                        <>
                          <img src={websiteQRCodeUrl} alt="QR Code" height={200} width={200} />
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>Website QR Code</div>
                          <div style={appMutedTextStyle}>
                            Share this QR code on printed materials so visitors can reach your site quickly.
                          </div>
                        </>
                      ) : (
                        <>
                          <i className="pi pi-qrcode" style={{ fontSize: "2rem", color: "#94a3b8" }} />
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>QR code available after publish</div>
                          <div style={appMutedTextStyle}>
                            Once the website is live, a shareable QR code will appear here.
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

          </AccordionTab>

          <AccordionTab headerTemplate={residentHeaderTemplate}>
            {residentsLoading ? (
              <div className="col-12 text-center d-flex justify-content-center align-items-center">
                <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
              </div>
            ) : (
              <>
                <div style={{ ...dashboardSectionCardStyle, overflow: "hidden", background: "#ffffff" }}>
                  <div style={{ ...appAccentPanelStyle, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }} className="flex flex-wrap justify-content-between align-items-center gap-3">
                    <div>
                      <div style={{ fontWeight: 700, color: "#0f172a" }}>Resident Directory</div>
                      <div className="mt-2" style={appMutedTextStyle}>
                        {showArchivedResidents
                          ? "Review archived residents. Switch back to active residents to manage current care plans, reminders, and documents."
                          : "Add residents here and open each profile to manage care plans, reminders, and documents."}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {archivedResidentCount > 0 ? (
                        <div className="flex align-items-center gap-2">
                          <label htmlFor="archived-residents-toggle" className="font-medium">
                            Archived ({archivedResidentCount})
                          </label>
                          <InputSwitch
                            inputId="archived-residents-toggle"
                            checked={showArchivedResidents}
                            onChange={(event) => setShowArchivedResidents(!!event.value)}
                            aria-label="Show archived residents"
                          />
                        </div>
                      ) : null}
                      <Button icon="pi pi-plus" label="New Resident" className="p-button-outlined" severity="warning" rounded
                        onClick={() => onAddResidentClick()}
                      />
                    </div>
                  </div>
                  <div style={{ padding: "1rem" }}>
                    <div style={appDataTableWrapperStyle}>
                      <DataTable
                        value={residentTable}
                        emptyMessage={showArchivedResidents
                          ? "No archived residents."
                          : "No active residents yet. Add a resident to get started."}
                        showHeaders={false}
                      >
                        <Column header="Profile" body={residentProfileItemTemplate}></Column>
                      </DataTable>
                    </div>
                  </div>
                </div>
              </>
            )}
          </AccordionTab>

          {/* Caregiver Connections is temporarily hidden until this workflow is reintroduced. */}

          {/* <AccordionTab headerTemplate={hiringProfileHeaderTemplate}>
            {hiringProfileLoader ? (
              <div className="col-12 text-center d-flex justify-content-center align-items-center">
                <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
              </div>
            ) : (
              <Card title={
                <div className="flex justify-content-end">
                  <Button icon="pi pi-pencil" label="Edit" className="p-button-outlined" severity="warning" rounded
                    onClick={() => setOpenEditHiringProfileModal(true)}
                  />
                </div>
              }
                subTitle={
                  <div>
                    Manage your caregiver hiring profile to attract qualified candidates.
                    Caregivers can view this profile during their job search.
                    <br />
                    <a onClick={() => navigate(RouterAddress.SEARCH_JOB)} style={{ cursor: 'pointer' }}> Click here</a> to see how your hiring profile appears to caregivers.
                  </div>
                }>
                {
                  caregiverHiringProfileData ? (
                    <div>
                      <Divider>
                        <i className="pi pi-briefcase mr-2"></i>
                        <strong>Basic Details</strong>
                      </Divider>
                      <div className="mb-2"><strong>Looking to Hire?:</strong> {caregiverHiringProfileData.lookingToHire ? "Yes" : "No"}</div>
                      <div className="mb-2"><strong>Job Title:</strong> {caregiverHiringProfileData.jobTitle || "N/A"}</div>
                      <div className="mb-2"><strong>Job Description:</strong> {caregiverHiringProfileData.jobDescription || "N/A"}</div>

                      <Divider>
                        <i className="pi pi-check-circle mr-2"></i>
                        <strong>Required Qualifications:</strong>
                      </Divider>
                      <div className="mb-2"><strong>Skills:</strong> {caregiverHiringProfileData.skills && caregiverHiringProfileData.skills.length > 0 ? caregiverHiringProfileData.skills.map(skill => skill.name).join(", ") : "N/A"}</div>
                      <div className="mb-2"><strong>Certifications:</strong> {caregiverHiringProfileData.certifications && caregiverHiringProfileData.certifications.length > 0 ? caregiverHiringProfileData.certifications.map(cert => cert.name).join(", ") : "N/A"}</div>
                      <Divider>
                        <i className="pi pi-dollar mr-2"></i>
                        <strong>Compensation & Availability:</strong>
                      </Divider>
                      <div className="mb-2"><strong>Expected Salary Range ($/hr):</strong> {caregiverHiringProfileData.expectedSalaryRange || "N/A"}</div>
                      <div className="mb-2"><strong>Relocation Willingness:</strong> {caregiverHiringProfileData.relocationWillingness ? "Yes" : "No"}</div>
                      <div className="mb-2"><strong>Living Arrangements:</strong> {caregiverHiringProfileData.livingArrangements ? "Yes" : "No"}</div>
                      <div className="mb-2"><strong>Available Day Shifts:</strong> {caregiverHiringProfileData.availableDayShifts && caregiverHiringProfileData.availableDayShifts.length > 0 ? caregiverHiringProfileData.availableDayShifts.map(day => day.name).join(", ") : "N/A"}</div>
                      <div className="mb-2"><strong>Available Night Shifts:</strong> {caregiverHiringProfileData.availableNightShifts && caregiverHiringProfileData.availableNightShifts.length > 0 ? caregiverHiringProfileData.availableNightShifts.map(night => night.name).join(", ") : "N/A"}</div>
                    </div>
                  ) : (
                    <p>No hiring profile available.</p>
                  )
                }
              </Card>
            )}
          </AccordionTab>

          <AccordionTab headerTemplate={clientAvailabilityHeaderTemplate}>
            {clientAvailabilityLoader ? (
              <div className="col-12 text-center d-flex justify-content-center align-items-center">
                <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
              </div>
            ) : (
              <Card subTitle={
                <div>
                  Manage your listings to attract potential families. Families can view this profile during their search.
                  <br />
                  <a onClick={() => navigate(RouterAddress.LISTINGS)} style={{ cursor: 'pointer' }}> Click here</a> to see how your listings appears to families.
                </div>
              }
                title={
                  <div className="flex justify-content-end">
                    <Button icon="pi pi-pencil" label="Edit" className="p-button-outlined" severity="warning" rounded
                      onClick={() => setOpenEditClientAvailabilityModal(true)}
                    />
                  </div>
                }>
                {
                  clientAvailabilityProfileData ? (
                    <div>
                      <Divider>
                        <i className="pi pi-home mr-2"></i>
                        <strong>Availability Details</strong>
                      </Divider>
                      <div className="mb-2"><strong>Number of Rooms Available:</strong> {clientAvailabilityProfileData.noOfRoomsAvailable || "N/A"}</div>
                      <div className="mb-2"><strong>Description:</strong> {clientAvailabilityProfileData.description || "N/A"}</div>
                      <Divider>
                        <i className="pi pi-check mr-2"></i>
                        <strong>Acceptance Criteria</strong>
                      </Divider>
                      <div className="mb-2">
                        <strong>Acceptance Criterias:</strong>&nbsp;
                        {clientAvailabilityProfileData.acceptanceCriterias && clientAvailabilityProfileData.acceptanceCriterias.length > 0 ?
                          clientAvailabilityProfileData.acceptanceCriterias.map(criteria => criteria.name).join(", ") : "N/A"}</div>
                      <Divider>
                        <i className="pi pi-times mr-2"></i>
                        <strong>Non-Acceptance Criteria</strong>
                      </Divider>
                      <div className="mb-2">
                        <strong>Non-Acceptance Criterias:</strong>&nbsp;
                        {clientAvailabilityProfileData.nonAcceptanceCriterias && clientAvailabilityProfileData.nonAcceptanceCriterias.length > 0 ?
                          clientAvailabilityProfileData.nonAcceptanceCriterias.map(criteria => criteria.name).join(", ") : "N/A"}</div>
                    </div>
                  ) : (
                    <p>No client availability details are available.</p>
                  )
                }
              </Card>
            )}

          </AccordionTab> */}
        </Accordion>
      </div>

      <Dialog
        header={renderSurfaceHeader("Change Website URL", "Use a simple, memorable address that is easy to share with families.", "pi pi-link")}
        visible={openWebsiteUrlEditorModal}
        style={mobile ? { width: '92vw' } : appDialogStyle}
        contentStyle={appDialogContentStyle}
        headerStyle={appDialogHeaderStyle}
        onHide={() => setOpenWebsiteUrlEditorModal(false)}
        footer={renderDialogFooter({
          errorMessage: newWebsiteUrlUpdateError,
          loading: updatingWebsiteUrl,
          onCancel: () => setOpenWebsiteUrlEditorModal(false),
          onSubmit: onUpdateWebsiteUrlClick,
          submitLabel: "Update URL",
        })}
      >
        <div className="flex flex-column gap-3">
          <div style={appInnerCardStyle}>
            <div style={{ fontWeight: 700, color: "#0f172a" }}>Current URL</div>
            <div className="mt-2" style={appMutedTextStyle}>{copyUrl || "No public URL available yet"}</div>
          </div>
          <div style={appNeutralPanelStyle}>
            <div style={{ fontWeight: 700, color: "#0f172a" }}>Choose a new path</div>
            <div className="mt-3 flex flex-column md:flex-row gap-2 align-items-start md:align-items-center">
              <span style={appMutedTextStyle}>{window.location.origin}/home/{businessData?.state}/</span>
              <InputText
                value={newWebsiteUrl}
                onChange={(e) => setNewWebsiteUrl(normalizePlainText(e.target.value, { maxLength: 80 }))}
                placeholder="all-spaces-will-be-replaced"
                className="w-full"
              />
            </div>
          </div>
          <div style={appInnerCardStyle}>
            <div style={{ fontWeight: 700, color: "#0f172a" }}>Preview</div>
            <div className="mt-2" style={appMutedTextStyle}>
              {window.location.origin}/home/{businessData?.state}/{getWebsiteName(newWebsiteUrl)}
            </div>
          </div>
        </div>
      </Dialog>

      <AddResidentModal
        homeId={homeId}
        facilityType={businessData?.facilityType}
        isOpen={openAddResidentModal}
        toggleModal={setOpenAddResidentModal}
        flag={residentFlag}
        setFlag={setResidentFlag}
      />

      <EditHomeProfileModal
        isOpen={openEditModal}
        toggleModal={() => {
          setOpenEditModal(false);
          setEditData(null);
        }}
        addFlag={flag}
        setAddFlag={setFlag}
        homeDetails={editData}
      />
      <LinkedLookupModal
        homeId={homeId}
        isOpen={openLinkedLookupModal}
        toggleModal={setOpenLinkedLookupModal}
        flag={linkedLookupFlag}
        setFlag={setLinkedLookupFlag}
        lookUpType={RESOURCE_LINK_TYPE.HOME_CAREGIVER}
      />
      <CaregiverHiringProfileModal
        homeId={homeId}
        isOpen={openEditHiringProfileModal}
        toggleModal={setOpenEditHiringProfileModal}
        flag={updateHiringProfileFlag}
        setFlag={setUpdateHiringProfileFlag}
        caregiverHiringProfileData={caregiverHiringProfileData}
      />
      <ClientAvailabilityProfileModal
        homeId={homeId}
        isOpen={openEditClientAvailabilityModal}
        toggleModal={setOpenEditClientAvailabilityModal}
        flag={updateClientAvailabilityFlag}
        setFlag={setUpdateClientAvailabilityFlag}
        clientAvailabilityProfileData={clientAvailabilityProfileData}
      />
      <WebsiteInquiriesModal
        inquiries={websiteInquiries}
        isOpen={openWebsiteInquiriesModal}
        flag={websiteInquiryFlag}
        setFlag={setWebsiteInquiryFlag}
        toggleModal={setOpenWebsiteInquiriesModal}
      />
      <WebsiteStatisticsModal
        homeId={homeId}
        isOpen={openWebsiteStatisticsModal}
        toggleModal={setOpenWebsiteStatisticsModal}
      />
      <UserFeedbackModal
        userId={feedbckToUserId}
        isOpen={openUserFeedbackModal}
        toggleModal={setOpenUserFeedbackModal}
      />
    </div >
  );
};

export default HomeDetails;
